"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type CoinStats,
  EMPTY_STATS,
  flipMany,
  flipOnce,
  recordFlips,
  type Side,
} from "@/lib/tool-kit/random-tools/coin";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const MAX_MULTI_FLIPS = 1000;
const FLIP_ANIMATION_MS = 600;

interface Batch {
  count: number;
  heads: number;
  tails: number;
}

function percent(part: number, whole: number, fmt: NumberFormatter): string {
  if (whole === 0) {
    return "0%";
  }
  return `${fmt((part / whole) * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const COIN_FLIP_STRINGS = {
  flipping: "Flipping…",
  flipCoin: "Flip Coin",
  flipManyAtOnce: "Flip many at once",
  numberOfFlips1: "Number of flips (1–",
  /*
   * One message each, because a sentence split around a count cannot be pluralised.
   *
   * These were `{strings.flip}{count}{strings.coins}` and `{strings.batchOf}{count}
   * {strings.flips}`, which render "Flip 1 Coins" and "Batch of 1 flips" in English and
   * cannot be fixed by any translation — a translator only ever sees the two halves.
   */
  flipCoins: "one: Flip {count} Coin|other: Flip {count} Coins",
  reset: "Reset",
  batchOfFlips: "one: Batch of {count} flip|other: Batch of {count} flips",
  headsCount: "one: {count} head ({percent})|other: {count} heads ({percent})",
  tailsCount: "one: {count} tail ({percent})|other: {count} tails ({percent})",
  totalFlips: "Total flips",
  heads2: "Heads",
  tails2: "Tails",
  longestStreak: "Longest streak",
  streakHeads: "{count}× heads",
  streakTails: "{count}× tails",
  everyFlipUsesMathRandom:
    "Every flip is one unbiased bit from your browser's cryptographic random source: an exact 50% chance of heads and 50% chance of tails, and no flip is influenced by the ones before it.",
} as const;

export default function CoinFlipWidget() {
  const [lastSide, setLastSide] = useState<Side | null>(null);
  const [flipping, setFlipping] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [multiCount, setMultiCount] = useState(10);
  const [lastBatch, setLastBatch] = useState<Batch | null>(null);
  const [stats, setStats] = useState<CoinStats>(EMPTY_STATS);

  const totalHeads = stats.heads;
  const totalTails = stats.tails;
  const longestStreak = stats.longest;
  const totalFlips = totalHeads + totalTails;

  // Functional updates only: the single flip lands in a timeout, so a tally captured when
  // the button was pressed would be stale by then and would erase whatever happened since.
  function recordResults(results: Side[]) {
    setStats((prev) => recordFlips(prev, results));
  }

  function handleFlip() {
    if (flipping) {
      return;
    }
    const side = flipOnce();
    setFlipping(true);
    setRotation((prev) => prev + 1080);
    setTimeout(() => {
      setLastSide(side);
      setLastBatch(null);
      recordResults([side]);
      setFlipping(false);
    }, FLIP_ANIMATION_MS);
  }

  function handleMultiFlip() {
    if (flipping) {
      return;
    }
    const results = flipMany(multiCount);
    const heads = results.filter((side) => side === "heads").length;
    setLastBatch({
      count: results.length,
      heads,
      tails: results.length - heads,
    });
    setLastSide(null);
    recordResults(results);
  }

  function handleReset() {
    if (flipping) {
      return;
    }
    setLastSide(null);
    setLastBatch(null);
    setStats(EMPTY_STATS);
  }

  const strings = useToolUi(COIN_FLIP_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();

  let coinLabel = "?";
  if (flipping) {
    coinLabel = "…";
  } else if (lastSide !== null) {
    coinLabel = (
      lastSide === "heads" ? strings.heads2 : strings.tails2
    ).toUpperCase();
  }

  const streakTemplate =
    longestStreak.side === "heads" ? strings.streakHeads : strings.streakTails;
  const longestStreakLabel =
    longestStreak.side === null
      ? "—"
      : t(streakTemplate, { count: fmt(longestStreak.length) });

  return (
    <>
      {/* Coin */}
      <div className="flex flex-col items-center gap-6 rounded-lg border border-border bg-card p-8">
        <div
          aria-live="polite"
          className="flex h-40 w-40 items-center justify-center rounded-full border-4 border-primary bg-primary/10 font-bold text-2xl text-foreground transition-transform duration-500 ease-out"
          style={{ transform: `rotateX(${rotation}deg)` }}
        >
          {coinLabel}
        </div>
        <button
          className="rounded-lg bg-primary px-8 py-3 font-medium text-base text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={flipping}
          onClick={handleFlip}
          type="button"
        >
          {flipping ? strings.flipping : strings.flipCoin}
        </button>
      </div>
      {/* Multi flip */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.flipManyAtOnce}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <label
            className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm"
            htmlFor="multi-count"
          >
            {strings.numberOfFlips1}
            {MAX_MULTI_FLIPS})
            <input
              className="w-20 rounded border border-border bg-background px-2 py-1 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="multi-count"
              max={MAX_MULTI_FLIPS}
              min={1}
              onChange={(e) =>
                setMultiCount(
                  Math.min(
                    MAX_MULTI_FLIPS,
                    Math.max(1, parseIntegerInput(e.target.value) ?? 1)
                  )
                )
              }
              type="number"
              value={multiCount}
            />
          </label>
          <button
            className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
            disabled={flipping}
            onClick={handleMultiFlip}
            type="button"
          >
            {tPlural(strings.flipCoins, locale, multiCount)}
          </button>
          <button
            className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
            disabled={flipping || totalFlips === 0}
            onClick={handleReset}
            type="button"
          >
            {strings.reset}
          </button>
        </div>
      </section>
      {/* Batch result */}
      {lastBatch !== null && (
        <div className="rounded-lg border border-border bg-card p-6 text-center">
          <p className="text-muted-foreground text-sm">
            {tPlural(strings.batchOfFlips, locale, lastBatch.count)}
          </p>
          <p className="mt-2 font-semibold text-foreground text-xl md:text-2xl">
            {tPlural(strings.headsCount, locale, lastBatch.heads, {
              percent: percent(lastBatch.heads, lastBatch.count, fmt),
            })}
            {" · "}
            {tPlural(strings.tailsCount, locale, lastBatch.tails, {
              percent: percent(lastBatch.tails, lastBatch.count, fmt),
            })}
          </p>
        </div>
      )}
      {/* Stats */}
      {totalFlips > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label={strings.totalFlips} value={fmt(totalFlips)} />
          <StatCard
            label={strings.heads2}
            value={`${fmt(totalHeads)} (${percent(totalHeads, totalFlips, fmt)})`}
          />
          <StatCard
            label={strings.tails2}
            value={`${fmt(totalTails)} (${percent(totalTails, totalFlips, fmt)})`}
          />
          <StatCard label={strings.longestStreak} value={longestStreakLabel} />
        </div>
      )}
      <p className="text-muted-foreground text-sm leading-relaxed">
        {strings.everyFlipUsesMathRandom}
      </p>
    </>
  );
}
